import {
  RAIL_MICRO_REM,
  RAIL_PRICE_REM,
  RAIL_ROW_GAP_REM,
  type StorefrontSizeToken,
} from "@/domain/storefront-appearance";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { Badge, Price, Text } from "@/components/ui";
import type { OptionGroup, Price as PriceModel } from "@/types";

/**
 * True only when the offer carries a real, higher "original" price.
 *
 * The old test was `price.value !== price.originalValue`, which reads a MISSING
 * `originalValue` as a discount (`25 !== undefined`) and renders a strikethrough
 * badge whose original price is empty — a ghost "R$ " beside the real one. It
 * also called a price *increase* a discount. Both are excluded here.
 */
function hasRealDiscount(price: PriceModel): price is PriceModel & { originalValue: number } {
  return typeof price.originalValue === "number" && price.originalValue > price.value;
}

/** Rail typography, or the menu's 18px/`mt-2` when no size is set. */
function railType(size?: StorefrontSizeToken) {
  if (!size) {
    return {
      fontSize: "18px" as const,
      marginTop: undefined,
      labelSize: undefined,
      compact: false,
    };
  }
  return {
    fontSize: `${RAIL_PRICE_REM[size]}rem`,
    marginTop: `${RAIL_ROW_GAP_REM[size]}rem`,
    labelSize: `${RAIL_MICRO_REM[size]}rem`,
    compact: size === "compact",
  };
}

export default function ItemOfferPrice({
  price,
  minPrice,
  optionGroups,
  isOptionGroup,
  minPriceIfDiscounted,
  size,
  dense,
}: {
  price: PriceModel;
  minPrice?: number;
  optionGroups?: OptionGroup[];
  isOptionGroup?: boolean;
  minPriceIfDiscounted?: number;
  /**
   * RAIL size. Without it price stays at the menu's 18px — fine in a list row,
   * a banner on a too-narrow card.
   */
  size?: StorefrontSizeToken;
  /**
   * Compact menu (3 columns): stack the price pair without shrinking type —
   * rail `size` owns typography.
   */
  dense?: boolean;
}) {
  const { price: priceAppearance } = useStorefrontAppearance();
  const allGroupsAreOptional = optionGroups?.every((opg) => opg.minPermitted === 0);
  const priceCanRise = optionGroups?.some((opg) => opg.options?.some((opt) => (opt.price?.value ?? 0) > 0)) ?? false;
  const type = { ...railType(size), compact: Boolean(dense || size === "compact") };

  if (minPrice && isOptionGroup) {
    if (allGroupsAreOptional || !priceCanRise) {
      const groupsDiscounted = minPriceIfDiscounted !== undefined && minPriceIfDiscounted > minPrice;
      const original = groupsDiscounted
        ? minPriceIfDiscounted
        : hasRealDiscount(price)
          ? price.originalValue
          : undefined;

      return original !== undefined ? (
        <StrikethroughPrice original={original} current={minPrice} type={type} />
      ) : (
        <Price
          as="p"
          value={price.value}
          weight="bold"
          tone="secondary"
          leading="comfortable"
          style={{ fontSize: type.fontSize, marginTop: type.marginTop ?? "0.5rem" }}
        />
      );
    }

    return (
      <Text
        as="div"
        leading={type.compact ? "tight" : "comfortable"}
        className="flex flex-col"
        style={{ marginTop: type.marginTop ?? "0.5rem" }}
      >
        {priceAppearance.showFromLabel && (
          <Text tone="muted" clamp={2} style={type.labelSize ? { fontSize: type.labelSize } : undefined}>
            A partir de
          </Text>
        )}
        <div style={{ fontSize: type.fontSize }}>
          {minPriceIfDiscounted && minPrice < minPriceIfDiscounted ? (
            <Text as="div" weight="bold" className="flex flex-wrap items-baseline gap-x-2">
              <Price value={minPriceIfDiscounted} strike tone="muted" weight="semibold" />
              <Price value={minPrice} weight="bold" tone="secondary" leading="comfortable" />
            </Text>
          ) : (
            <Price as="p" value={minPrice} weight="bold" tone="secondary" leading="comfortable" />
          )}
        </div>
      </Text>
    );
  }

  return hasRealDiscount(price) ? (
    <StrikethroughPrice original={price.originalValue} current={price.value} type={type} />
  ) : (
    <Price
      as="p"
      value={price?.value ?? 0}
      weight="bold"
      tone="secondary"
      leading="comfortable"
      style={{ fontSize: type.fontSize, marginTop: type.marginTop ?? "0.5rem" }}
    />
  );
}

/** Strikethrough original price next to the discounted price badge. */
function StrikethroughPrice({
  original,
  current,
  type,
}: {
  original: number;
  current: number;
  type: ReturnType<typeof railType>;
}) {
  return (
    <div
      className="flex flex-wrap items-baseline"
      style={{ fontSize: type.fontSize, marginTop: type.marginTop ?? "0.5rem" }}
    >
      <Price as="p" value={original} strike tone="disabled" weight="bold" leading="comfortable" className="mr-2 w-fit" />
      <Badge variant="solid" tone="secondary" size="flush">
        <Price value={current} weight="bold" leading="comfortable" />
      </Badge>
    </div>
  );
}
